import React, { useState } from 'react';
import {
  Box,
  Group,
  Text,
  ActionIcon,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconInfoCircle,
  IconMenu2,
} from '@tabler/icons-react';
import NotificationDropdown from './NotificationDropdown';
import AboutModal from './AboutModal';
import { useSidebar } from '../contexts/SidebarContext';

interface TopHeaderProps {
  user: {
    username: string;
    email: string;
    roles?: string[];
    nombre?: string;
    apellidos?: string;
  };
  onLogout: () => void;
  sidebarWidth?: number;
  isMobile?: boolean;
  onMenuClick?: () => void;
}

const TopHeader: React.FC<TopHeaderProps> = ({ user, isMobile = false, onMenuClick }) => {
  const { colorScheme } = useMantineColorScheme();
  const { setCollapsed } = useSidebar();
  const [aboutOpen, setAboutOpen] = useState(false);

  // Función para obtener el nombre del usuario
  const getUserName = () => {
    if (user.nombre && user.apellidos) {
      return `${user.nombre} ${user.apellidos}`;
    }
    return user.username;
  };


  return (
    <Box
      style={{
        position: 'static',
        height: '70px',
        background: colorScheme === 'dark'
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.4))'
          : 'linear-gradient(135deg, rgba(247, 243, 238, 0.8), rgba(242, 237, 230, 0.6))',
        padding: isMobile ? '12px 16px' : '16px 32px',
        gap: isMobile ? '8px' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 1,
        borderBottom: `1px solid ${
          colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'
        }`,
      }}
    >
      {/* Left side - Botón de menú (móvil) + saludo (escritorio) */}
      <Group gap="sm" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
        {isMobile && (
          <ActionIcon
            variant="subtle"
            size={36}
            onClick={onMenuClick}
            aria-label="Abrir menú"
            style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26' }}
          >
            <IconMenu2 size={22} />
          </ActionIcon>
        )}
        {!isMobile && (
          <Box>
            <Text
              size="xs"
              ta="left"
              style={{
                color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                fontFamily: 'Inter, sans-serif',
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              Bienvenido,
            </Text>
            <Text
              size="sm"
              fw={700}
              ta="left"
              style={{
                color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                fontFamily: 'Inter, sans-serif',
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {getUserName()}
            </Text>
          </Box>
        )}
      </Group>

      {/* Spacer to keep profile/notifications on the right */}
      <Box style={{ flex: 1 }} />

      {/* Right side - Notifications and Profile */}
      <Group gap="sm" align="center">
        {/* Notifications */}
        <NotificationDropdown />

        {/* Help Button */}
        <ActionIcon
          variant="subtle"
          size={36}
          onClick={() => {
            setCollapsed(true);
            setAboutOpen(true);
          }}
          style={{
            backgroundColor: colorScheme === 'dark'
              ? 'rgba(30, 41, 59, 0.7)'
              : 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(40px) saturate(200%)',
            WebkitBackdropFilter: 'blur(40px) saturate(200%)',
            border: `1px solid ${
              colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'
            }`,
            borderRadius: '50%',
            color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
            boxShadow: colorScheme === 'dark'
              ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
          }}
        >
          <IconInfoCircle size={18} />
        </ActionIcon>
      </Group>

      <AboutModal opened={aboutOpen} onClose={() => setAboutOpen(false)} />
    </Box>
  );
};

export default TopHeader;